import { faBasketShoppingMagnifyingGlass } from '@awesome.me/kit-09b03e5df4/icons/kit/custom';
import {
  faBurgerGlass, faCarBus, faCircleUser, faCircleUserCircleUser, faClothesHanger, faHandHoldingSeedling,
  faHouseBuilding, faStore, faSyringe, faUserPolice,
} from '@fortawesome/pro-solid-svg-icons';
import { type FaIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { UnreachableCaseException } from '~/_shared/exceptions/unreachableCaseException';
import type { TranslationFnc } from '~/_shared/utils/hooks';
import { type AgsReportCountry } from '~/exportReport/exportReport.constants';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { ExportReportTemplateSelectorArea } from '~/exportReport/templateSelector/exportReportTemplateSelector.constants';
import type { Translations } from '~/translations/translations.type';
import bdp from '../template/converted/bdp.rt.yml';
import can_crm from '../template/converted/can_crm.rt.yml';
import can_cxp1 from '../template/converted/can_cxp1.rt.yml';
import can_cxp3 from '../template/converted/can_cxp3.rt.yml';
import can_cxs from '../template/converted/can_cxs.rt.yml';
import can_cxt from '../template/converted/can_cxt.rt.yml';
import crm from '../template/converted/crm.rt.yml';
import cxa from '../template/converted/cxa.rt.yml';
import cxf from '../template/converted/cxf.rt.yml';
import cxs from '../template/converted/cxs.rt.yml';
import cxt from '../template/converted/cxt.rt.yml';
import env from '../template/converted/env.rt.yml';
import gp5 from '../template/converted/gp5.rt.yml';
import hp from '../template/converted/hp.rt.yml';
import mex_mho from '../template/converted/mex_mho.rt.yml';
import mex_mp1 from '../template/converted/mex_mp1.rt.yml';
import xp1 from '../template/converted/xp1.rt.yml';
import xp5 from '../template/converted/xp5.rt.yml';

type TemplateSelectorItem = {
  id: string;
  template: ReportTemplate;
  icon: FaIcon;
  title: keyof Translations;
  description: keyof Translations;
};

const usaReports: TemplateSelectorItem[] = [{
  id: 'key-demographics',
  template: xp1,
  icon: faCircleUser,
  title: 'Key Demographics',
  description: 'Top-level demographic snapshot, current year only.',
}, {
  id: 'demographic-essentials',
  template: xp5,
  icon: faCircleUserCircleUser,
  title: 'Demographic Essentials',
  description: 'Full demographic breakdown including trends and projections.',
}, {
  id: 'crime-risk',
  template: crm,
  icon: faUserPolice,
  title: 'Crime Risk',
  description: 'Personal and property crime risk indices and grades.',
}, {
  id: 'health-profile',
  template: hp,
  icon: faSyringe,
  title: 'Health Profile',
  description: 'Demographics, vital statistics, and hospital data.',
}, {
  id: 'environmental-risk',
  template: env,
  icon: faHandHoldingSeedling,
  title: 'Environmental risk and topography',
  description: 'Climate, terrain, air quality, and natural hazard risk grades.',
}, {
  id: 'business-details-profile',
  template: bdp,
  icon: faStore,
  title: 'Business Detail Profile',
  description: 'Establishment and employee counts by industry.',
}, {
  id: 'cxs',
  template: cxs,
  icon: faHouseBuilding,
  title: 'Consumer Expenditures: Housing',
  description: 'Household shelter and utility spending with housing inventory.',
}, {
  id: 'cxf',
  template: cxf,
  icon: faBurgerGlass,
  title: 'Consumer Expenditures: Food and Grocery',
  description: 'Household spending on groceries, dining out, and drinks.',
}, {
  id: 'cxa',
  template: cxa,
  icon: faClothesHanger,
  title: 'Consumer Expenditures: Apparel',
  description: 'Household clothing and apparel spending by category.',
}, {
  id: 'cxt',
  template: cxt,
  icon: faCarBus,
  title: 'Consumer Expenditures: Transportation',
  description: 'Household transportation spending and commute data.',
}, {
  id: 'gp5',
  template: gp5,
  icon: faBasketShoppingMagnifyingGlass,
  title: 'Retail Gap Analysis',
  description: 'exportReport.templateSelector.gapDescription',
}];

const canadaReports: TemplateSelectorItem[] = [
  {
    id: 'key-demographics',
    template: can_cxp1,
    icon: faCircleUser,
    title: 'Key Demographics',
    description: 'Top-level demographic snapshot, current year only.',
  },
  {
    id: 'demographic-essentials',
    template: can_cxp3,
    icon: faCircleUserCircleUser,
    title: 'Demographic Essentials',
    description: 'Full demographic breakdown including trends and projections.',
  },
  {
    id: 'crime-risk',
    template: can_crm,
    icon: faUserPolice,
    title: 'Crime Risk',
    description: 'Personal and property crime risk indices and grades.',
  },
  {
    id: 'cxs',
    template: can_cxs,
    icon: faHouseBuilding,
    title: 'Consumer Expenditures: Housing',
    description: 'Household shelter and utility spending with housing inventory.',
  },
  {
    id: 'cxt',
    template: can_cxt,
    icon: faCarBus,
    title: 'Consumer Expenditures: Transportation',
    description: 'Household transportation spending and commute data.',
  },
];

const mexicoReports: TemplateSelectorItem[] = [{
  id: 'mex-key-demographics',
  template: mex_mp1,
  icon: faCircleUser,
  title: 'Key Demographics',
  description: 'Top-level demographic snapshot, current year only.',
}, {
  id: 'mex-cxs',
  template: mex_mho,
  icon: faHouseBuilding,
  title: 'Consumer Expenditures: Housing',
  description: 'Household shelter and utility spending with housing inventory.',
}];

export const getAreaReportItems = (area: ExportReportTemplateSelectorArea) => {
  switch (area) {
    case ExportReportTemplateSelectorArea.USA:
      return usaReports;
    case ExportReportTemplateSelectorArea.Canada:
      return canadaReports;
    case ExportReportTemplateSelectorArea.Mexico:
      return mexicoReports;
    default:
      throw new UnreachableCaseException(area, `Unknown AGS Report area '${area}'`);
  }
};

export const getReportAreaTranslation = (area: ExportReportTemplateSelectorArea, t: TranslationFnc) => {
  switch (area) {
    case ExportReportTemplateSelectorArea.Canada:
      return t('Canada');
    case ExportReportTemplateSelectorArea.USA:
      return t('USA');
    case ExportReportTemplateSelectorArea.Mexico:
      return t('Mexico');
    default:
      throw new UnreachableCaseException(area, `Unknown AGS Report area '${area}'`);
  }
};

export const getAgsReportCountryForArea = (area: ExportReportTemplateSelectorArea): AgsReportCountry => {
  switch (area) {
    case ExportReportTemplateSelectorArea.Mexico:
      return 'mex';
    case ExportReportTemplateSelectorArea.Canada:
      return 'can';
    case ExportReportTemplateSelectorArea.USA:
      return 'usa';
    default:
      throw new UnreachableCaseException(area, `Unknown AGS Report area '${area}'`);
  }
};
